/**
 * canvasVisibilite — le canvas est-il **montré**, et à qui (2026-08-13).
 *
 * ## Le mot « ouvert » portait trois choses à la fois
 *
 * Il n'existait qu'un booléen global (`useRedactionModeStore.enabled`, ce module
 * le remplace) et huit lecteurs s'en servaient pour répondre à trois questions
 * différentes :
 *
 * 1. *le panneau prend-il de la place à l'écran ?* — de l'affichage ;
 * 2. *quelle conversation détient ce document ?* — de la possession, qui vit
 *    déjà ailleurs et se dit `holderConversationId` ;
 * 3. *Hector a-t-il un canvas où écrire ?* — de la disponibilité, qui n'a rien à
 *    voir avec le fait que l'avocat regarde ou non.
 *
 * Les confondre produisait deux symptômes signalés à l'usage : refermer le
 * canvas dans une conversation le refermait dans **toutes**, et changer de
 * conversation faisait **suivre** le document — la scène cédait le canvas à
 * l'écran affiché, si bien que le document de A devenait celui de B au simple
 * fait de le regarder.
 *
 * ## Le modèle, en une phrase
 *
 * > **Plusieurs documents peuvent rester chauds. Un seul host est montré :
 * > celui de la session sélectionnée, là où vit son document.**
 *
 * D'où la règle unique de ce module :
 *
 * ```
 * visible ⟺ déployé ET session sélectionnée ET
 *            (le document n'appartient à personne OU l'écran affiche
 *             la conversation qui le détient)
 * ```
 *
 * Le déploiement reste global — c'est l'intention de l'avocat, « je veux mon
 * canvas », pas une donnée par fil. Ce qui devient conditionnel, c'est
 * l'**endroit** où cette intention se réalise. Corollaire recherché : il n'y a
 * plus rien d'accessible dans une conversation qui ne détient pas le document :
 * son résident reste chaud, mais parqué avec `aria-hidden` + `inert`.
 *
 * ## Ce que ce module ne fait PAS
 *
 * Il ne cède rien. Regarder n'est pas prendre — c'est précisément le couplage
 * qu'on supprime. La possession se prend par un **geste** : le bouton
 * d'ouverture de l'avocat (`ouvrirLeCanvasPourLAvocat`, `canvasOpening`) ou une
 * écriture d'agent (`openCanvasForAgentWrite`, `WebSocketPool`), tous deux
 * passant par une admission de résidence qui vérifie capacité et session exacte.
 *
 * ⚠️ **Ne jamais rebrancher la disponibilité agent sur ce module.** Un canvas
 * masqué détient toujours son document : dire à Hector « pas de canvas » parce
 * que l'avocat regarde ailleurs recréerait l'incident du 12/08 en miroir — il
 * répondrait dans le chat en croyant n'avoir nulle part où écrire. Cette
 * question-là se lit sur la possession (`canvasAgentState`).
 */

import * as React from 'react';
import { create } from 'zustand';
import { canvasDocumentRuntime } from './canvasDocumentRuntime';
import type { RestaurationIndisponible } from './canvasRestaurationDeBrouillon';
import type { CanvasSessionRegistrySnapshot } from './canvasSessionRegistry';

/**
 * Projection UI d'une admission physique. Elle ne remplace pas le registre :
 * celui-ci reste l'autorité sur les scopes SuperDoc. Cette information évite
 * seulement qu'un premier montage froid ressemble à un clic sans effet, avant
 * que le registre puisse sélectionner son premier résident.
 */
export type CanvasResidenceRequestSource = 'agent-document-load' | 'canvas-residence';

export type CanvasResidenceRequestState = {
  readonly conversationId: string;
  readonly status: 'pending' | 'failed';
  /** L'échec agent garde le split ; une résidence générique reste discrète. */
  readonly source: CanvasResidenceRequestSource;
};

interface CanvasVisibiliteStore {
  /**
   * L'avocat veut-il un canvas ? Global à l'onglet, et c'est voulu : c'est une
   * intention, pas une propriété d'un fil. L'endroit où elle se réalise est
   * décidé par le slot (`useCanvasSlotResolution`).
   */
  deploye: boolean;
  /**
   * Le canvas a-t-il été déployé au moins une fois dans cet onglet ?
   *
   * Ce n'est pas un doublon de `deploye` : depuis #856 le canvas prend
   * **résidence** dans la coque — le masquer rend le document sélectionné
   * invisible, pas absent. Ce drapeau ouvre donc le chargement différé ; un
   * changement explicite de document ou de compte peut ensuite remonter
   * l'éditeur sans le réinitialiser.
   *
   * Sa raison d'être est le poids : SuperDoc pèse ~1,7 Mo compressés, chargés
   * paresseusement. Sans ce verrou, tout avocat paierait ce chargement sur
   * chaque page de l'application, y compris sans jamais rédiger.
   */
  aDejaEteDeploye: boolean;
  /** Numéro monotone de geste d'ouverture, y compris quand déjà déployé. */
  requestSequence: number;
  /** Admission en cours ou en échec, pour la projection immédiate du split. */
  residenceRequest: CanvasResidenceRequestState | null;
  /**
   * Les fils dont le canvas a été masqué parce que l'avocat est parti sur
   * « Nouvelle conversation », pas par la croix. Seul ce masquage-là autorise
   * une réouverture automatique au retour dans le fil
   * (`rouvrirLeCanvasAuRetourDansLeFil`) : la croix est une décision de
   * l'avocat, qu'aucun retour ne doit défaire.
   *
   * Un souvenir PAR fil, consommé au retour dans ce fil seulement : un canvas
   * ouvert entre-temps dans un autre fil (une pièce ouverte par Hector, un
   * second « Nouvelle conversation ») ne doit pas faire oublier le premier.
   */
  masquesEnQuittantLeFil: Readonly<Record<string, true>>;
  setDeploye: (v: boolean) => void;
  beginResidenceRequest: (conversationId: string, source: CanvasResidenceRequestSource) => void;
  settleResidenceRequest: (conversationId: string) => void;
  failResidenceRequest: (conversationId: string) => void;
}

export const useCanvasVisibiliteStore = create<CanvasVisibiliteStore>((set) => ({
  deploye: false,
  aDejaEteDeploye: false,
  requestSequence: 0,
  residenceRequest: null,
  masquesEnQuittantLeFil: {},
  setDeploye: (v: boolean) => set((s) => ({
    deploye: v,
    aDejaEteDeploye: s.aDejaEteDeploye || v,
    requestSequence: v ? s.requestSequence + 1 : s.requestSequence,
    // Un nouveau geste « Ouvrir » est le retry explicite après un échec. Une
    // admission encore en cours, elle, doit rester visible et se joindre.
    residenceRequest: v
      ? s.residenceRequest?.status === 'failed' ? null : s.residenceRequest
      : null,
  })),
  beginResidenceRequest: (conversationId, source) => set({
    residenceRequest: { conversationId, status: 'pending', source },
  }),
  settleResidenceRequest: (conversationId) => set((s) => (
    s.residenceRequest?.conversationId === conversationId
      ? { residenceRequest: null }
      : {}
  )),
  failResidenceRequest: (conversationId) => set((s) => (
    s.residenceRequest?.conversationId === conversationId
      ? {
          residenceRequest: {
            conversationId,
            status: 'failed',
            source: s.residenceRequest.source,
          },
        }
      : {}
  )),
}));

/**
 * L'échec d'un import DOCX est le seul qui puisse retenir le split : il garde
 * la trace de la demande dont l'avocat vient de rejoindre la conversation.
 */
export function isAgentDocumentLoadFailureForConversation(
  residenceRequest: CanvasResidenceRequestState | null,
  conversationId: string | null,
): residenceRequest is CanvasResidenceRequestState & {
  readonly status: 'failed';
  readonly source: 'agent-document-load';
} {
  return residenceRequest?.conversationId === conversationId
    && residenceRequest.status === 'failed'
    && residenceRequest.source === 'agent-document-load';
}

/**
 * Ce que l'unique emplacement de l'écran peint — nommé par sa CONVERSATION (#1336).
 *
 * L'union portait `documentSessionId` / `targetDocumentSessionId` ; la
 * conversation étant l'identité du document logique, ce sont les mêmes deux
 * places, sous le seul nom qui reste vrai. Aucune époque de montage n'y entre :
 * un emplacement peint un DOCUMENT, et le remontage de ce document ne change
 * pas ce qui doit y être peint.
 */
export type CanvasSlotResolution =
  | {
      readonly kind: 'hidden';
      readonly conversationId: null;
      readonly targetConversationId: null;
    }
  | {
      readonly kind: 'visible';
      readonly conversationId: string;
      readonly targetConversationId: null;
    }
  | {
      readonly kind: 'transitioning';
      readonly conversationId: null;
      readonly targetConversationId: string;
    }
  | {
      /** Le tool agent a échoué après que le split a déjà rejoint le fil. */
      readonly kind: 'failed';
      readonly conversationId: null;
      readonly targetConversationId: string;
    };

/** Document exact que l'écran courant peut accueillir dans son unique slot. */
export function useVisibleCanvasConversationId(
  conversationAffichee: string | null,
): string | null {
  return useCanvasSlotResolution(conversationAffichee).conversationId;
}

/**
 * État complet du slot, y compris la fenêtre où le document de la conversation
 * affichée est encore en train d'être hydraté.
 *
 * Le registre garde l'ancien résident sélectionné pendant `requestResident`
 * afin de pouvoir le restaurer si le chargement de la cible échoue. Cette
 * sélection transactionnelle ne doit toutefois jamais être peinte dans la
 * conversation cible : elle produirait exactement le flash de l'acte A lors
 * de l'ouverture du document vierge B. Le troisième état permet au composant
 * d'afficher un emplacement neutre sans masquer le canvas ni réafficher son
 * bouton d'ouverture.
 */
export function useCanvasSlotResolution(
  conversationAffichee: string | null,
): CanvasSlotResolution {
  const deploye = useCanvasVisibiliteStore((s) => s.deploye);
  const residenceRequest = useCanvasVisibiliteStore((s) => s.residenceRequest);
  const [registry, setRegistry] = React.useState(() => canvasDocumentRuntime.getSessionsSnapshot());
  React.useLayoutEffect(() => {
    // Une navigation peut remonter ce lecteur après que CanvasResidence a déjà
    // sélectionné son résident. S'abonner seul manquerait cette notification et
    // garderait le slot en transition jusqu'à la prochaine mutation du runtime.
    const unsubscribe = canvasDocumentRuntime.subscribeSessions(setRegistry);
    setRegistry(canvasDocumentRuntime.getSessionsSnapshot());
    return unsubscribe;
  }, []);
  return resolveCanvasSlotResolution({ deploye, conversationAffichee, registry, residenceRequest });
}

/**
 * Résout l'état du slot pendant un échange de résidence.
 *
 * Une cible logique réservée prouve un geste documentaire. Tant qu'elle n'est
 * pas acceptée, le registre peut encore désigner le sortant pour les besoins
 * du rollback ; la projection UI passe néanmoins en état neutre pour ne jamais
 * montrer le document d'une autre conversation.
 */
export function resolveCanvasSlotResolution(input: {
  deploye: boolean;
  conversationAffichee: string | null;
  registry: CanvasSessionRegistrySnapshot;
  residenceRequest?: CanvasResidenceRequestState | null;
}): CanvasSlotResolution {
  const { deploye, conversationAffichee, registry, residenceRequest = null } = input;
  if (!deploye) {
    return { kind: 'hidden', conversationId: null, targetConversationId: null };
  }
  const requestedTarget = registry.sessions.find(
    (session) => session.conversationId === conversationAffichee,
  );
  const requestTargetsConversation = residenceRequest?.conversationId === conversationAffichee;
  if (isAgentDocumentLoadFailureForConversation(residenceRequest, conversationAffichee)) {
    // Le dock a déjà ouvert ce split pour ce tool agent. Refermer le panneau
    // ferait disparaître l'échec derrière un chat ordinaire : l'occupant écran
    // le rend donc explicitement, sans prolonger le spinner de transition.
    return {
      kind: 'failed',
      conversationId: null,
      targetConversationId: residenceRequest.conversationId,
    };
  }
  if (requestTargetsConversation && residenceRequest?.status === 'failed') {
    // Les erreurs de résidence ordinaires gardent leur verdict/toast dédié.
    // Les laisser en transition prolongerait un spinner sans source DOCX ni
    // action supplémentaire à expliquer dans ce split.
    return { kind: 'hidden', conversationId: null, targetConversationId: null };
  }
  if (!registry.visibleConversationId) {
    return requestedTarget && requestTargetsConversation
      ? {
          kind: 'transitioning',
          conversationId: null,
          targetConversationId: requestedTarget.conversationId,
        }
      : { kind: 'hidden', conversationId: null, targetConversationId: null };
  }
  const visible = registry.sessions.find(
    (session) => session.conversationId === registry.visibleConversationId,
  );
  if (!visible) return { kind: 'hidden', conversationId: null, targetConversationId: null };
  // ⚠️ La branche `conversationId === null` a été RETIRÉE avec ce qui la
  // produisait (#1336) : `beginMount()` frappait une identité sans propriétaire,
  // il n'y en a plus à frapper, et `CanvasSessionSnapshot.conversationId` a
  // cessé d'être nullable. Le commentaire d'avant demandait de la retirer « avec
  // lui » : c'est fait.
  if (visible.conversationId === conversationAffichee) {
    return {
      kind: 'visible',
      conversationId: visible.conversationId,
      targetConversationId: null,
    };
  }
  // ⚠️ La seconde condition — « la cible EST le visible » — a disparu, devenue
  // tautologique (#1336). La cible est trouvée PAR `conversationAffichee`, et
  // l'on n'arrive ici que si le visible en diffère : les deux ne peuvent plus
  // coïncider. Elle rendrait la même valeur avec le défaut et sans lui.
  if (!requestedTarget) {
    return { kind: 'hidden', conversationId: null, targetConversationId: null };
  }
  return {
    kind: 'transitioning',
    conversationId: null,
    targetConversationId: requestedTarget.conversationId,
  };
}

/**
 * Ce qui occupe l'emplacement Canvas de l'écran — **une seule décision** (#1333).
 *
 * L'écran avait deux autorités : la résolution de slot ci-dessus, et un test
 * ajouté à côté pour le refus de restauration, dont la valeur entrait ensuite
 * dans le même booléen « le split s'ouvre-t-il ? ». Les deux répondaient à la
 * même question, sans qu'aucun type ne les tienne ensemble : ajouter un
 * troisième occupant demandait de se souvenir des deux.
 *
 * ⚠️ Le refus n'occupe l'emplacement que lorsque **rien d'autre** ne l'occupe.
 * C'est structurel ici, et c'était auparavant une condition à maintenir en
 * miroir de celle du panneau : `RedactionPanel` affiche le même verdict en
 * bandeau quand un résident est là, et les deux surfaces ne doivent jamais
 * apparaître ensemble.
 */
export type CanvasScreenOccupancy =
  | { readonly kind: 'aucune'; readonly conversationId: null; readonly refus: null }
  | { readonly kind: 'resident'; readonly conversationId: string; readonly refus: null }
  | { readonly kind: 'transition'; readonly conversationId: null; readonly refus: null }
  | { readonly kind: 'chargement-echoue'; readonly conversationId: null; readonly refus: null }
  | {
      readonly kind: 'refus-de-restauration';
      readonly conversationId: null;
      readonly refus: RestaurationIndisponible;
    };

export function resolveCanvasScreenOccupancy(input: {
  readonly slot: CanvasSlotResolution;
  readonly deploye: boolean;
  readonly verdictDeRestauration: RestaurationIndisponible | null;
}): CanvasScreenOccupancy {
  switch (input.slot.kind) {
    case 'visible':
      return { kind: 'resident', conversationId: input.slot.conversationId, refus: null };
    case 'transitioning':
      // Le sortant est encore sélectionné pour permettre un rollback. Ouvrir
      // l'emplacement sans rien y peindre est ce qui empêche d'afficher
      // brièvement A dans B.
      return { kind: 'transition', conversationId: null, refus: null };
    case 'failed':
      return { kind: 'chargement-echoue', conversationId: null, refus: null };
    case 'hidden':
      // Un refus n'ouvre l'emplacement que si le canvas a été explicitement
      // déployé. Une recherche en échec au simple affichage reste donc discrète ;
      // le clic « Canvas », lui, déploie sans acquérir de session et montre la
      // raison sans permettre à CanvasResidence de monter une page blanche.
      return input.deploye && input.verdictDeRestauration !== null
        ? {
            kind: 'refus-de-restauration',
            conversationId: null,
            refus: input.verdictDeRestauration,
          }
        : { kind: 'aucune', conversationId: null, refus: null };
  }
}

/** Keeps the ordinary Canvas affordance available while a different outgoing host occupies the slot. */
export function shouldOfferCanvasOpenControl(input: {
  /** Le document de la conversation affichée, s'il en existe un. */
  activeConversationId: string | null;
  /** Le document réellement peint dans l'emplacement. */
  slotConversationId: string | null;
  /** Le tool DOCX agent a échoué et attend une relance qui porte sa source. */
  agentDocumentLoadFailed?: boolean;
}): boolean {
  // Le slot neutre a deux causes : une résidence réellement en vol, ou un
  // document déjà réservé mais non sélectionné dans la conversation regardée.
  // Cette projection ne les distingue pas. Dans les deux cas, le geste
  // « Canvas » est légitime : `requestResident` coalesce une résidence déjà
  // demandée et n'évince jamais directement le document sortant. Le masquer
  // rendait au contraire un document froid impossible à reprendre.
  // Le seul cas où le contrôle ne peut pas coalescer est l'échec du tool
  // DOCX : l'ouverture humaine repartirait sans sa source et créerait un
  // Canvas vierge à la place du diagnostic.
  if (input.agentDocumentLoadFailed) return false;
  // Pendant « Nouvelle conversation » ou le rendu intermédiaire d'une
  // navigation, aucune session ne peut être résolue par l'identifiant de route.
  // Un slot déjà occupé reste pourtant le canvas visible : le bouton ne doit
  // pas se superposer à ce panneau en prenant `null` pour une autre session.
  if (input.activeConversationId === null) {
    return input.slotConversationId === null;
  }
  return input.slotConversationId !== input.activeConversationId;
}

/** Le déploiement, pour les lecteurs hors de React. */
export function lireCanvasDeploye(): boolean {
  return useCanvasVisibiliteStore.getState().deploye;
}

/**
 * Masquer le canvas — le geste de l'avocat sur la croix du panneau.
 *
 * **Masquer n'est pas fermer** : le document reste ouvert, il garde son porteur,
 * son brouillon et sa session temps réel ; Hector peut continuer d'y écrire. Il
 * n'existe d'ailleurs aucun geste qui *supprime* le document — « Nouveau » le
 * remplace, ce qui est autre chose et se confirme.
 */
export type DecisionDeMasquageCanvas =
  | { readonly statut: 'masquee'; readonly conversationId: string }
  | {
      readonly statut: 'ignoree';
      readonly raison: 'document-non-visible';
      readonly conversationId: string;
      readonly visibleConversationId: string | null;
    };

export function masquerLeCanvas(conversationId: string): DecisionDeMasquageCanvas {
  const visibleConversationId = canvasDocumentRuntime.getSessionsSnapshot()
    .visibleConversationId;
  if (visibleConversationId !== conversationId) {
    // Une croix rendue sous A peut survivre jusqu'au commit qui sélectionne B.
    // Masquer le booléen global ici ferait disparaître B au nom d'un callback A.
    return {
      statut: 'ignoree',
      raison: 'document-non-visible',
      conversationId,
      visibleConversationId,
    };
  }
  canvasDocumentRuntime.maskSession(conversationId);
  useCanvasVisibiliteStore.getState().setDeploye(false);
  return { statut: 'masquee', conversationId };
}

/**
 * Masquer le canvas parce que l'avocat quitte le fil pour « Nouvelle
 * conversation » — et s'en souvenir, pour le lui rendre à son retour s'il y
 * attend encore un document (`rouvrirLeCanvasAuRetourDansLeFil`).
 */
export function masquerLeCanvasEnQuittantLeFil(conversationId: string): DecisionDeMasquageCanvas {
  const decision = masquerLeCanvas(conversationId);
  if (decision.statut === 'masquee') {
    useCanvasVisibiliteStore.setState((s) => ({
      masquesEnQuittantLeFil: { ...s.masquesEnQuittantLeFil, [conversationId]: true },
    }));
  }
  return decision;
}
